import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  DocumentationPills,
  GlobalData,
  GlobalState,
  ParsedFieldsByObject,
} from './globalReducerTypes';
import { rollupReducers } from './rollupReducers';
import { automationReducers } from './automationReducers';
import { RootState } from '../index';
import { assignmentReducers } from './assignmentReducers';
import { alertsReducers } from './alertsReducers';
import { funnelMapsReducer } from './funnelMapsReducer';
import { dedupMatchingReducers } from './dedupMatchingReducers';
import { scheduledAssignmentReducers } from './scheduledAssignmentReducers';
import { scheduledReportReducers } from './scheduledReportReducers';
import {
  calcLayouts,
  calcParsedRecordTypes,
  calcParsedRules,
} from '../../components/parser/parserUtils';
import { ObjectData } from '../../components/parser/ParserTypes';
import { scheduledJobReducers } from './scheduledJobReducers';
import { marketingAttributionReducer } from './marketingAttributionReducer';

const initialState: GlobalState = {
  environments: {},
  loadingStatuses: {},
};

export const globalSlice = createSlice({
  name: 'global',
  initialState,
  reducers: {
    setGlobalData: (
      state,
      action: PayloadAction<{
        crmOrgId: string;
        globalData: GlobalData;
      }>,
    ) => {
      const { crmOrgId, globalData } = action.payload;
      const { funnelMap, ...rest } = globalData;

      if (!state.environments[crmOrgId]) {
        state.environments[crmOrgId] = {
          data: globalData,
          ui: {
            shouldRefresh: false,
          },
        };
        state.loadingStatuses[crmOrgId] = {
          ...state.loadingStatuses[crmOrgId],
          isLoading: undefined,
          error: undefined,
        };

        return;
      }

      state.environments[crmOrgId].ui.shouldRefresh = false;
      state.environments[crmOrgId].data = {
        ...rest,
        funnelMap: state.environments[crmOrgId].data?.funnelMap ?? funnelMap,
      };
      state.loadingStatuses[crmOrgId] = {
        ...state.loadingStatuses[crmOrgId],
        isLoading: undefined,
        error: undefined,
      };
    },
    requestRefreshGlobal: (state, action: PayloadAction<{ crmOrgId: string }>) => {
      const { crmOrgId } = action.payload;
      if (!state.environments[crmOrgId]?.ui) {
        state.environments[crmOrgId] = {
          ui: {
            shouldRefresh: true,
          },
        };
      }
      state.environments[crmOrgId].ui.shouldRefresh = true;
    },
    setGlobalIsLoading: (state, action: PayloadAction<{ crmOrgId: string }>) => {
      const { crmOrgId } = action.payload;
      state.loadingStatuses[crmOrgId] = state.loadingStatuses[crmOrgId] || {};
      state.loadingStatuses[crmOrgId].isLoading = true;
      state.loadingStatuses[crmOrgId].error = undefined;
    },
    setGlobalError: (state, action: PayloadAction<{ crmOrgId: string; error: any }>) => {
      const { crmOrgId, error } = action.payload;
      state.loadingStatuses[crmOrgId] = state.loadingStatuses[crmOrgId] || {};
      state.loadingStatuses[crmOrgId].isLoading = false;
      state.loadingStatuses[crmOrgId].error = error;
    },
    addParsedDataToDocumentation: (
      state,
      action: PayloadAction<{
        response: ObjectData[];
        objectName: string;
        crmOrgId: string;
        fieldsParsedOnDemand: ParsedFieldsByObject;
        pills: DocumentationPills;
      }>,
    ) => {
      const { objectName, crmOrgId, response, fieldsParsedOnDemand, pills } = action.payload;
      const newObjectRules = calcParsedRules(response);
      const newObjectRecordTypes = calcParsedRecordTypes(response);
      const newObjectLayouts = calcLayouts(response);
      const oldData = state.environments[crmOrgId].data?.documentation ?? {};

      if (state.environments[crmOrgId].data?.documentation) {
        state.environments[crmOrgId].data.documentation = {
          layouts: { ...oldData.layouts, ...newObjectLayouts },
          parsedRules: [...(oldData.parsedRules ?? []), ...newObjectRules],
          parsedRecordTypes: [...(oldData.parsedRecordTypes ?? []), ...newObjectRecordTypes],
          parsedObjectNames: [...(oldData.parsedObjectNames ?? []), objectName],
          parsedFields: { ...(oldData.parsedFields ?? {}), ...fieldsParsedOnDemand },
          pills: { ...(oldData.pills ?? {}), ...pills },
        };
      }
    },
    ...rollupReducers,
    ...automationReducers,
    ...assignmentReducers,
    ...alertsReducers,
    ...dedupMatchingReducers,
    ...funnelMapsReducer,
    ...scheduledAssignmentReducers,
    ...scheduledReportReducers,
    ...scheduledJobReducers,
    ...marketingAttributionReducer,
  },
});

export const selectLoadingStatuses = (state: RootState) => state.global.loadingStatuses;

export const selectShouldRefresh = (crmOrgId: string) => (state: RootState) =>
  state.global.environments[crmOrgId]?.ui.shouldRefresh;

export const selectEnvironments = (state: RootState) => state.global.environments;

export const selectConfigurationCanvasFunnelMap = (crmOrgId: string) => (state: RootState) =>
  state.global.environments[crmOrgId]?.data?.funnelMap;

export const selectParserInfo = (crmOrgId: string) => (state: RootState) =>
  state.global.environments[crmOrgId]?.data?.documentation;

export const selectParsedFields = (crmOrgId: string) => (state: RootState) =>
  state.global.environments[crmOrgId]?.data?.documentation?.parsedFields;

export const selectParsedRecordTypes = (crmOrgId: string) => (state: RootState) =>
  state.global.environments[crmOrgId]?.data?.documentation?.parsedRecordTypes;

export const selectParsedRules = (crmOrgId: string) => (state: RootState) =>
  state.global.environments[crmOrgId]?.data?.documentation?.parsedRules;

export const selectOnlyActiveParsedRules = (crmOrgId: string) => (state: RootState) =>
  state.global.environments[crmOrgId]?.data?.documentation?.onlyActiveParsedRules;

export const selectRelatedObjectsByObjectName = (crmOrgId: string) => (state: RootState) =>
  state.global.environments[crmOrgId]?.data?.documentation?.relatedObjects;

export const selectElementDependencies = (crmOrgId: string) => (state: RootState) =>
  state.global.environments[crmOrgId]?.data?.elementDependencies;

export const selectOrgSweepFields = (crmOrgId: string) => (state: RootState) =>
  state.global.environments[crmOrgId]?.data?.sweepFields;

export const selectFieldByFieldId = (crmOrgId: string, fieldId?: string) => (state: RootState) => {
  if (!fieldId) return undefined;
  return state.global.environments[crmOrgId]?.data?.sweepFields[fieldId];
};

export const {
  setGlobalData,
  requestRefreshGlobal,
  setGlobalIsLoading,
  setGlobalError,

  addRollup,
  updateRollup,
  deleteRollup,
  updateDeployStatus,

  addAutomation,
  updateAutomation,
  toggleAutomation,
  deleteAutomation,
  updateAutomationDeployStatus,

  addAssignment,
  updateAssignment,
  toggleAssignment,
  deleteAssignment,
  updateAssignmentDeployStatus,

  addAlert,
  updateAlert,
  toggleAlert,
  deleteAlert,
  updateAlertDeployStatus,

  addDedupMatching,
  updateDedupMatching,
  toggleDedupMatching,
  deleteDedupMatching,
  updateDedupMatchingDeployStatus,

  setEnvironmentFunnelMap,
  addRecordTypeDataEntries,
  setLeadingFieldForRecordType,
  setRecordTypeDataValues,
  clearLeadingField,
  hideRecordType,

  addScheduledAssignment,
  updateScheduledAssignment,
  toggleScheduledAssignment,
  deleteScheduledAssignment,
  updateScheduledAssignmentDeployStatus,

  addScheduledReport,
  updateScheduledReport,
  toggleScheduledReport,
  deleteScheduledReport,
  updateScheduledReportDeployStatus,

  addScheduledJob,
  updateScheduledJob,
  toggleScheduledJob,
  deleteScheduledJob,
  updateScheduledJobDeployStatus,

  addMarketingAttributionModel,
  updateMarketingAttributionModel,
  toggleMarketingAttributionModel,
  deleteMarketingAttributionModel,
  updateMarketingAttributionModelDeployStatus,
  setMarketingAttributionModels,

  addParsedDataToDocumentation,
} = globalSlice.actions;

export default globalSlice.reducer;
